import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import "./Hero.css";

export default function AboutEducation() {
  const [activeIndex, setActiveIndex] = useState(null);

  const educationList = [
    {
      degree: "Bachelor of Engineering in Information Technology",
      school: "Nepal College of Information Technology (NCIT)",
      schoolUrl: "https://www.ncit.edu.np/",
      university: "Pokhara University",
      universityUrl: "https://pu.edu.np/",
      year: "2019 - 2025",
      details:
        "Focused on software development, system design, networking, and real-world engineering applications with an emphasis on practical implementation and problem solving.",
      tags: ["Full-Stack", "System Design", "DBMS", "Networking", "Project Development"],
    },
  ];

  const experienceList = [
    {
      company: "Yaks Inc.",
      role: "Full-Stack Developer",
      period: "June 2024 – May 2025",
      bullets: [
        "Developed and maintained scalable web applications.",
        "Built REST APIs and supported Linux-based deployments.",
        "Assisted in production releases and resolved production issues.",
        "Improved application performance and reliability.",
      ],
    },
    {
      company: "WiseAdmit",
      role: "Junior QA Engineer",
      period: "August 2025 – May 2026",
      bullets: [
        "Performed manual, regression, and cross-browser testing.",
        "Reported and verified bugs to improve product quality.",
        "Collaborated closely with development teams during releases.",
      ],
    },
  ];

  const achievements = [
    "Top 20 Finalist – National Business Idea Competition",
    "Successfully deployed and maintained multiple production web applications.",
    "Experience across Development, QA, and Production Support.",
  ];

  const toggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <main className="relative min-h-screen px-6 py-20 text-gray-100 overflow-hidden animated-bg">
      <div className="absolute top-[-100px] left-[-100px] w-[300px] h-[300px] bg-blue-500/20 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-100px] right-[-100px] w-[300px] h-[300px] bg-purple-500/20 blur-[120px] rounded-full" />

      <motion.section
        id="about"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-3xl mx-auto text-center mb-28 pt-10"
      >
        <h2 className="text-3xl font-bold mb-6">About Me</h2>

        <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto">
          I’m an Information Technology graduate with professional experience in Full-Stack Development, Quality Assurance, Linux-based deployments, Docker, and Production Support. I enjoy building scalable web applications, deploying production systems, troubleshooting complex issues, optimizing performance, and ensuring software quality. My interests extend beyond coding into Linux systems, infrastructure, DevOps practices, cloud deployment, and application reliability.
        </p>
      </motion.section>

      <motion.section
        id="experience"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-5xl mx-auto mb-24"
      >
        <h2 className="text-3xl font-bold mb-10 text-center">Professional Experience</h2>

        <div className="grid gap-6 md:grid-cols-2">
          {experienceList.map((item, index) => (
            <div
              key={index}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
            >
              <p className="text-sm uppercase tracking-[0.2em] text-blue-400">{item.period}</p>
              <h3 className="mt-2 text-xl font-semibold text-white">{item.company}</h3>
              <p className="mt-1 text-sm text-gray-300">{item.role}</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-400">
                {item.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex gap-2">
                    <span className="text-blue-400">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </motion.section>

      <section id="education" className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-12 text-center">Education</h2>

        <div className="space-y-6">
          {educationList.map((edu, idx) => {
            const isOpen = activeIndex === idx;

            return (
              <motion.div
                key={idx}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="cursor-pointer group"
                onClick={() => toggle(idx)}
              >
                <motion.div
                  layout
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 transition-all duration-300 hover:border-blue-500/40 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{edu.degree}</h3>
                      <p className="text-sm text-gray-400">{edu.school}</p>
                    </div>

                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      className="text-gray-400"
                    >
                      ▼
                    </motion.span>
                  </div>

                  <p className="text-xs text-gray-500 mt-1">{edu.year}</p>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 border-t border-white/10 pt-4 space-y-4">
                          <div className="text-sm">
                            <a
                              href={edu.schoolUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:underline"
                            >
                              Visit Institution ↗
                            </a>

                            {edu.university && (
                              <>
                                {" • "}
                                <a
                                  href={edu.universityUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:underline"
                                >
                                  University ↗
                                </a>
                              </>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {edu.tags.map((tag, i) => (
                              <span
                                key={i}
                                className="px-3 py-1 text-xs rounded-full bg-blue-500/10 text-blue-400"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>

                          <p className="text-gray-400 text-sm leading-relaxed">{edu.details}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <motion.section
        id="achievements"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-4xl mx-auto mt-24"
      >
        <h2 className="text-3xl font-bold mb-8 text-center">Achievements</h2>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <ul className="space-y-3 text-gray-300">
            {achievements.map((item, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-blue-400">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.section>

      <motion.section
        id="license"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-4xl mx-auto mt-28"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Engineering Credentials</h2>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-10 rounded-2xl text-center relative overflow-hidden hover:scale-[1.02] transition">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-20" />

          <h3 className="text-2xl font-semibold mb-4 relative z-10">Licensed IT Engineer</h3>

          <p className="text-gray-400 text-sm mb-4 relative z-10">Nepal Engineering Council</p>

          <a
            href="https://nec.gov.np/registration/98513"
            target="_blank"
            rel="noopener noreferrer"
            className="relative z-10 inline-block px-6 py-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 hover:scale-105 transition"
          >
            Verify Engineering License ↗
          </a>
        </div>
      </motion.section>
    </main>
  );
}